import * as api from "../transport/api/message-api.js";
import * as agentApi from "../transport/api/agent-api.js";
import * as store from "./stores/message-store.js";
import * as sessionStore from "./stores/session-store.js";
import * as runFacade from "./run-facade.js";

let activeSessionId = null;
let activeController = null; // 当前流式请求的 AbortController,用于 Stop 按钮
let activeAgentRunId = null;
let activeAssistantMessageId = null;
// 当前活跃流相关的临时 typing ID（用于 stop 时清理残留指示器）。
// sendAgentMessage 用 typing-${ts}，regenerate 用 regen-${messageId}。
let activeTypingId = null;
const streamStateListeners = new Set();

/**
 * 订阅流式状态变化(开始/结束),用于 UI 切换发送/停止按钮。
 * @param {(isStreaming: boolean) => void} cb
 */
export function onStreamStateChange(cb) {
  streamStateListeners.add(cb);
  return () => streamStateListeners.delete(cb);
}

function setStreaming(isStreaming) {
  for (const cb of streamStateListeners) {
    try { cb(isStreaming); } catch { /* listener error 不影响主流程 */ }
  }
}

export function setActiveSession(id) {
  // 切换会话时必须中断当前 SSE，否则旧流的 onEvent 会写入新会话的 store、
  // 且 activeController 会被后续发送覆盖造成孤儿控制器。
  if (activeSessionId !== id) {
    stopActiveStream();
  }
  activeSessionId = id;
}

/**
 * 中断当前活跃的流式生成。后端 res.on('close') 会触发 finalize 'ABORTED'。
 */
export function stopActiveStream() {
  if (activeController) {
    activeController.abort();
    if (activeAgentRunId) {
      runFacade.updateLiveRun(activeAgentRunId, { status: "CANCELLED", finishReason: "ABORTED", phase: "cancelled", completedAt: new Date().toISOString() });
      activeAgentRunId = null;
    }
    if (activeAssistantMessageId != null) {
      store.finalizeAssistant(activeAssistantMessageId, { latencyMs: null, finishReason: "ABORTED" });
      store.setAgentStatus(activeAssistantMessageId, { state: "cancelled", context: { assistant_message_id: activeAssistantMessageId } });
      activeAssistantMessageId = null;
    }
    // 清理残留的 typing 指示器（session_ready 后 message 前点击 Stop 的边界 case）
    if (activeTypingId) {
      store.removeMessage(activeTypingId);
      activeTypingId = null;
    }
    activeController = null;
    setStreaming(false);
  }
}

/**
 * AFV-001：断流/网络失败时就地标记失败气泡 + run 状态，
 * 供 sendAgentMessage 和 regenerateAgentMessage 的 catch 块复用。
 */
function markRunFailed({ messageId, pendingAssistantId, runId, err }) {
  const failedId = messageId ?? pendingAssistantId;
  if (failedId != null) {
    store.upsertAssistantMessage({ messageId: failedId });
    store.appendAgentTimelineStep(failedId, {
      kind: "source_failure",
      title: "连接中断",
      summary: `${err.message || "连接提前中断，回复可能不完整"}；可点击“重新生成”重试。`,
    });
    store.finalizeAssistant(failedId, { latencyMs: null, finishReason: "error" });
  }
  if (runId) {
    runFacade.updateLiveRun(runId, { status: "FAILED", errorMessage: err.message || "连接中断", completedAt: new Date().toISOString() });
    if (activeAgentRunId === runId) activeAgentRunId = null;
  }
  activeAssistantMessageId = null;
}

/**
 * Agent 模式发送消息。
 * 复用同一消息 store,额外产生 agent_state / agent_memory_load /
 * agent_skill_activate 等事件,并在当前消息里聚合显示。
 */
export async function sendAgentMessage({ sessionId, content, attachments, artifactIds = [], clientUserMessageId = null, onError, onStreamEvent = null }) {
  // 如果当前还有未结束的流（用户在流式期间点了发送），先中断旧流避免控制器孤儿。
  if (activeController) stopActiveStream();
  activeSessionId = sessionId;
  const controller = new AbortController();
  activeController = controller;
  let pendingAssistantId = null;
  const TYPING_ID = `typing-${Date.now()}`;
  activeTypingId = TYPING_ID;
  let agentState = "";
  let agentContext = {};
  let runId = null;
  let displayMessageId = TYPING_ID;
  setStreaming(true);
  try {
    await agentApi.postAgentRunSSE({
      sessionId,
      message: content,
      attachments,
      artifactIds,
      clientMessageId: clientUserMessageId,
      signal: controller.signal,
      onEvent: (evt) => {
        if (activeSessionId !== sessionId) return;
        if (evt.event === "session_ready") {
          pendingAssistantId = evt.assistant_message_id;
          activeAssistantMessageId = pendingAssistantId;
          if (evt.user_message_id != null && clientUserMessageId) {
            store.promoteUserMessage({ fromId: clientUserMessageId, messageId: evt.user_message_id });
          }
          runId = evt.run_id || null;
          if (runId) {
            activeAgentRunId = runId;
            runFacade.startLiveRun({ runId, sessionId, assistantMessageId: pendingAssistantId, title: content });
          }
          store.appendTypingIndicator(TYPING_ID);
        } else if (evt.event === "agent_state") {
          agentState = evt.state;
          agentContext = evt.context || { round: evt.round };
          store.setAgentStatus(displayMessageId, { state: agentState, context: agentContext });
          if (runId) runFacade.updateLiveRun(runId, {
            status: agentState === "queued" ? "QUEUED" : "RUNNING",
            phase: agentState,
            currentRound: evt.round == null ? null : Number(evt.round) + 1,
            ...(agentState === "queued" ? {} : { startedAt: new Date().toISOString() }),
          });
        } else if (evt.event === "agent_memory_load") {
          store.setAgentMemoryLoad(displayMessageId, evt.summaries || (evt.was_compressed != null ? [`记忆压缩: ${evt.was_compressed}`] : []));
        } else if (evt.event === "agent_skill_activate") {
          store.appendAgentSkill(displayMessageId, {
            skillId: evt.skill_code || evt.skill_id,
            name: evt.skill_name || evt.name,
            version: evt.skill_version || null,
            purpose: evt.purpose || "",
            tools: Array.isArray(evt.tools) ? evt.tools : [],
            unavailableTools: Array.isArray(evt.unavailable_tools) ? evt.unavailable_tools : [],
            versionSource: evt.version_source || "stable",
          });
        } else if (evt.event === "agent_quality_check") {
          store.setAgentQuality(displayMessageId, {
            schemaVersion: evt.schema_version,
            status: evt.status,
            score: evt.score,
            coverage: evt.coverage,
            skills: Array.isArray(evt.skills) ? evt.skills : [],
            repair: evt.repair || null,
          });
        } else if (evt.event === "agent_timeline") {
          store.appendAgentTimelineStep(displayMessageId, { round: evt.round, kind: evt.kind, title: evt.title, summary: evt.summary });
        } else if (evt.event === "message") {
          const msgId = evt.message_id || pendingAssistantId;
          if (msgId && !store.hasMessage(msgId)) store.promoteAgentMessage({ fromId: TYPING_ID, messageId: msgId, agentState, agentContext });
          displayMessageId = msgId || displayMessageId;
          // 一旦真实消息 id 出现，清理 typing 标记，避免 stop 时重复 remove。
          if (msgId && activeTypingId === TYPING_ID) activeTypingId = null;
          // 允许 answer 为空字符串（text_reset / 过程轮不写主气泡）
          if (msgId && Object.prototype.hasOwnProperty.call(evt, "answer")) {
            store.replaceAssistantContent(msgId, evt.answer || "");
          }
        } else if (evt.event === "message_end") {
          store.finalizeAssistant(evt.message_id, {
            latencyMs: evt.latency_ms,
            finishReason: evt.finish_reason,
          });
          if (runId) {
            const finishReason = evt.finish_reason || "stop";
            const status = finishReason === "fallback" ? "FALLBACK" : finishReason === "error" ? "FAILED" : "COMPLETED";
            runFacade.updateLiveRun(runId, { status, finishReason, completedAt: new Date().toISOString() });
            if (activeAgentRunId === runId) activeAgentRunId = null;
          }
          if (String(activeAssistantMessageId) === String(evt.message_id)) activeAssistantMessageId = null;
        } else if (evt.event === "tool_call") {
          // Agent 过程统一由 agent_timeline 呈现；保留事件供旧客户端兼容。
        } else if (evt.event === "tool_result") {
          // Agent 过程统一由 agent_timeline 呈现；保留事件供旧客户端兼容。
        } else if (evt.event === "approval_required") {
          // D1 受控 Tool 审批框架：弹窗并回传审批结果
          handleApprovalRequired(evt);
        } else if (evt.event === "done") {
          const sessionPatch = {
            id: evt.session_id,
            messageCount: evt.message_count,
            lastActiveAt: evt.last_active_at || new Date().toISOString(),
          };
          if (evt.session_title != null) sessionPatch.title = evt.session_title;
          sessionStore.upsertSession(sessionPatch);
        } else if (evt.event === "error") {
          store.removeMessage(TYPING_ID);
          if (runId) runFacade.updateLiveRun(runId, { status: "FAILED", errorMessage: evt.message || "Agent 运行失败", completedAt: new Date().toISOString() });
          if (activeAgentRunId === runId) activeAgentRunId = null;
          activeAssistantMessageId = null;
          onError?.(evt);
        }
        onStreamEvent?.(evt);
      },
    });
  } catch (err) {
    if (err.name === "AbortError") {
      // 停止生成：清理 typing 指示器，避免残留三个跳动圆点。
      store.removeMessage(TYPING_ID);
      if (activeTypingId === TYPING_ID) activeTypingId = null;
      return;
    }
    store.removeMessage(TYPING_ID);
    if (activeTypingId === TYPING_ID) activeTypingId = null;
    // AFV-001：断流/网络失败时，若助手消息已创建，就地留下可回看的失败气泡与告警步骤。
    markRunFailed({ messageId: displayMessageId, pendingAssistantId, runId, err });
    onError?.({ code: err.code || "NETWORK", message: err.message });
    throw err;
  } finally {
    if (activeTypingId === TYPING_ID) activeTypingId = null;
    if (activeController === controller) {
      activeController = null;
      setStreaming(false);
    }
  }
  return controller;
}

export function setMessages(messages) {
  store.setMessages(messages);
}

/**
 * Agent 模式重新生成指定 assistant 消息。
 */
export async function regenerateAgentMessage({ sessionId, messageId, onError, onStreamEvent = null }) {
  // 如果当前还有未结束的流（用户在流式期间点了重新生成），先中断旧流，避免两条 SSE 并发写入同一 store。
  if (activeController) stopActiveStream();
  activeSessionId = sessionId;
  const controller = new AbortController();
  activeController = controller;
  setStreaming(true);
  let runId = null;
  let displayMessageId = messageId;
  const REGEN_TYPING_ID = `regen-${messageId}`;
  activeTypingId = REGEN_TYPING_ID;
  store.replaceAssistantContent(messageId, "");
  store.appendTypingIndicator(REGEN_TYPING_ID);
  try {
    await agentApi.regenerateAgentMessageSSE({
      sessionId,
      messageId,
      signal: controller.signal,
      onEvent: (evt) => {
        if (activeSessionId !== sessionId) return;
        if (evt.event === "session_ready") {
          runId = evt.run_id || null;
          activeAssistantMessageId = messageId;
          if (runId) {
            activeAgentRunId = runId;
            runFacade.startLiveRun({ runId, sessionId, assistantMessageId: messageId, title: "重新生成回复" });
          }
        } else if (evt.event === "agent_state") {
          if (runId) runFacade.updateLiveRun(runId, { phase: evt.state, currentRound: evt.round == null ? null : Number(evt.round) + 1 });
        } else if (evt.event === "agent_timeline") {
          store.appendAgentTimelineStep(displayMessageId, { round: evt.round, kind: evt.kind, title: evt.title, summary: evt.summary });
        } else if (evt.event === "agent_memory_load") {
          store.setAgentMemoryLoad(messageId, evt.summaries || (evt.was_compressed != null ? [`记忆压缩: ${evt.was_compressed}`] : []));
        } else if (evt.event === "agent_skill_activate") {
          store.appendAgentSkill(messageId, {
            skillId: evt.skill_code || evt.skill_id,
            name: evt.skill_name || evt.name,
            version: evt.skill_version || null,
            purpose: evt.purpose || "",
            tools: Array.isArray(evt.tools) ? evt.tools : [],
            unavailableTools: Array.isArray(evt.unavailable_tools) ? evt.unavailable_tools : [],
            versionSource: evt.version_source || "stable",
          });
        } else if (evt.event === "agent_quality_check") {
          store.setAgentQuality(messageId, {
            schemaVersion: evt.schema_version,
            status: evt.status,
            score: evt.score,
            coverage: evt.coverage,
            skills: Array.isArray(evt.skills) ? evt.skills : [],
            repair: evt.repair || null,
          });
        } else if (evt.event === "message") {
          store.removeMessage(`regen-${messageId}`);
          if (activeTypingId === REGEN_TYPING_ID) activeTypingId = null;
          if (Object.prototype.hasOwnProperty.call(evt, "answer")) {
            store.replaceAssistantContent(messageId, evt.answer || "");
          }
        } else if (evt.event === "message_end") {
          store.removeMessage(`regen-${messageId}`);
          if (activeTypingId === REGEN_TYPING_ID) activeTypingId = null;
          store.finalizeAssistant(messageId, { latencyMs: evt.latency_ms, finishReason: evt.finish_reason });
          if (runId) {
            const finishReason = evt.finish_reason || "stop";
            runFacade.updateLiveRun(runId, { status: finishReason === "fallback" ? "FALLBACK" : finishReason === "error" ? "FAILED" : "COMPLETED", finishReason, completedAt: new Date().toISOString() });
            if (activeAgentRunId === runId) activeAgentRunId = null;
          }
          if (String(activeAssistantMessageId) === String(messageId)) activeAssistantMessageId = null;
        } else if (evt.event === "tool_call") {
          // Agent 时间线统一呈现在被重新生成的回复气泡内。
        } else if (evt.event === "tool_result") {
          // Agent 时间线统一呈现在被重新生成的回复气泡内。
        } else if (evt.event === "approval_required") {
          // D1 受控 Tool 审批框架：弹窗并回传审批结果
          handleApprovalRequired(evt);
        } else if (evt.event === "done") {
          // no session title update on regenerate
        } else if (evt.event === "error") {
          store.removeMessage(`regen-${messageId}`);
          if (runId) runFacade.updateLiveRun(runId, { status: "FAILED", errorMessage: evt.message || "Agent 运行失败", completedAt: new Date().toISOString() });
          if (activeAgentRunId === runId) activeAgentRunId = null;
          activeAssistantMessageId = null;
          onError?.(evt);
        }
        onStreamEvent?.(evt);
      },
    });
  } catch (err) {
    if (err.name === "AbortError") {
      store.removeMessage(`regen-${messageId}`);
      if (activeTypingId === REGEN_TYPING_ID) activeTypingId = null;
      return;
    }
    store.removeMessage(`regen-${messageId}`);
    if (activeTypingId === REGEN_TYPING_ID) activeTypingId = null;
    // AFV-001：重新生成断流同样就地标记失败，留下可回看的告警步骤。
    markRunFailed({ messageId, runId, err });
    onError?.({ code: err.code || "NETWORK", message: err.message });
    throw err;
  } finally {
    if (activeTypingId === REGEN_TYPING_ID) activeTypingId = null;
    if (activeController === controller) {
      activeController = null;
      setStreaming(false);
    }
  }
  return controller;
}

// T6: 取当前 store 里最后一个持久化的 message id(数字 id,跳过 tmp-/typing- 等临时 id)
// 用于断线重连时作为 after 参数请求增量消息
export function getLastPersistedMessageId() {
  const { messages } = store.getMessageState();
  for (let i = messages.length - 1; i >= 0; i--) {
    const id = messages[i]?.id;
    if (id == null) continue;
    const s = String(id);
    if (/^\d+$/.test(s)) return Number(id);
  }
  return null;
}

// T6: 断线重连后的增量同步 — 拉取 lastSeenId 之后的所有消息并合并到 store
// 返回新增条数;失败时除 console.warn 外通过 onSyncError 回调通知调用方（AFV-006）
export async function syncMessagesAfter(sessionId, lastSeenId, { isCurrent = () => true, signal, onSyncError } = {}) {
  if (!sessionId || !lastSeenId) return 0;
  try {
    let cursor = lastSeenId;
    let merged = 0;
    // 每页 100 条，最多补齐 2000 条；超长离线期间会在下一次同步继续。
    for (let page = 0; page < 20; page++) {
      const result = await api.getMessagesAfter(sessionId, cursor, { signal });
      if (!isCurrent()) return merged;
      const messages = result.messages || [];
      merged += store.mergeServerMessages(messages);
      if (!result.hasMore || messages.length === 0) break;
      cursor = Number(result.nextAfter || messages[messages.length - 1]?.id || cursor);
    }
    return merged;
  } catch (err) {
    console.warn("[aichat] syncMessagesAfter failed:", err?.message || err);
    if (err?.name !== "AbortError") onSyncError?.(err);
    return 0;
  }
}

// ── D1 受控 Tool 审批框架：审批确认弹窗（预留骨架） ──
// 收到 approval_required SSE 事件后弹出，用户确认/拒绝后通过 POST /api/agent/approval 回传。
// 当前为预留骨架 UI：完整的跨线程审批闭环待业务 Tool 接入时实现。
const APPROVAL_MODAL_ID = "aichat-approval-modal";

function createApprovalElement(tagName, { className = "", id = "", text = "", attrs = {} } = {}) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (id) element.id = id;
  if (text) element.textContent = text;
  for (const [name, value] of Object.entries(attrs)) {
    element.setAttribute(name, String(value));
  }
  return element;
}

function ensureApprovalModal() {
  let modal = document.getElementById(APPROVAL_MODAL_ID);
  if (modal) return modal;
  modal = document.createElement("div");
  modal.id = APPROVAL_MODAL_ID;
  modal.className = "aichat-approval-modal";
  const titleId = "aichat-approval-title";
  const backdrop = createApprovalElement("div", { className: "aichat-approval-backdrop" });
  const dialog = createApprovalElement("div", {
    className: "aichat-approval-dialog",
    attrs: { role: "dialog", "aria-modal": "true", "aria-labelledby": titleId },
  });
  const actions = createApprovalElement("div", { className: "aichat-approval-actions" });
  actions.append(
    createApprovalElement("button", {
      className: "aichat-approval-reject",
      text: "拒绝",
      attrs: { type: "button" },
    }),
    createApprovalElement("button", {
      className: "aichat-approval-confirm",
      text: "确认执行",
      attrs: { type: "button" },
    }),
  );
  dialog.append(
    createApprovalElement("div", { className: "aichat-approval-title", id: titleId, text: "工具执行审批" }),
    createApprovalElement("div", { className: "aichat-approval-tool" }),
    createApprovalElement("div", { className: "aichat-approval-args" }),
    createApprovalElement("div", { className: "aichat-approval-hint", text: "该操作为写操作，需要你确认后才会执行。" }),
    actions,
  );
  modal.append(backdrop, dialog);
  document.body.appendChild(modal);
  return modal;
}

/**
 * 显示审批确认弹窗，返回 Promise<boolean>（true=确认，false=拒绝）。
 */
function showApprovalModal({ toolName, argsSummary }) {
  return new Promise((resolve) => {
    const modal = ensureApprovalModal();
    const dialog = modal.querySelector(".aichat-approval-dialog");
    modal.querySelector(".aichat-approval-tool").textContent = `工具：${toolName || "未知"}`;
    try {
      const parsed = typeof argsSummary === "string" ? JSON.parse(argsSummary) : argsSummary;
      modal.querySelector(".aichat-approval-args").textContent =
        "参数：" + (typeof parsed === "string" ? parsed : JSON.stringify(parsed));
    } catch {
      modal.querySelector(".aichat-approval-args").textContent =
        argsSummary ? `参数：${argsSummary}` : "";
    }
    const close = (approved) => {
      modal.classList.remove("show");
      resolve(approved);
    };
    modal.querySelector(".aichat-approval-confirm").onclick = () => close(true);
    modal.querySelector(".aichat-approval-reject").onclick = () => close(false);
    modal.querySelector(".aichat-approval-backdrop").onclick = () => close(false);
    modal.classList.add("show");
  });
}

/**
 * 处理 approval_required SSE 事件：弹窗 + 回传审批结果。
 */
async function handleApprovalRequired(evt) {
  const callId = evt.call_id;
  const toolName = evt.tool_name;
  const argsSummary = evt.args_summary;
  if (!callId) return;
  try {
    const approved = await showApprovalModal({ toolName, argsSummary });
    await agentApi.submitApproval({ callId, approved });
  } catch (err) {
    // 审批回传失败不影响主流程（当前为预留骨架，跨线程闭环未打通时这里会 404）
    console.warn("[aichat] approval submit failed:", err?.message || err);
  }
}
